/* eslint-disable sonarjs/cognitive-complexity */
import * as React from 'react';

import { BaSeDialogContext } from '@base/react';
import { useInterviewController } from '../../../hooks/controllers/interview-controller';
import { useMessage } from '../../../hooks/messages/useMessage';
import { useInterviewContext } from './interview-context';

const ValidationsContext = React.createContext(null);

export function ValidationsProvider({ children }) {
  const { interview, formData, radioId, saveBasicDataInterview } =
    useInterviewContext();

  const { showNotification } = React.useContext(BaSeDialogContext);

  const { updateConditionViewVideo, updateConditionAceptedTerm } =
    useInterviewController();

  const { handleError } = useMessage();

  const [statusVideo, setStatusVideo] = React.useState(false);
  const [term, setTerm] = React.useState(false);
  const [aceptEmancipated, setAceptEmancipated] = React.useState(false);
  const [year, setYear] = React.useState({ id: 0, label: 'Selecione' });

  const [viewVideo, setViewVideo] = React.useState(false);

  const [formErrors, setFormErrors] = React.useState({});

  React.useEffect(() => {
    if (!interview) return;
    setViewVideo(interview.viewedVideo);
    setStatusVideo(interview.viewedVideo)
    setTerm(interview.acceptedTerm);
  }, [interview]);

  const isUnderage = (birthDate) => {
    if (!birthDate) return false;

    const today = new Date();
    const birth = new Date(birthDate);

    if (isNaN(birth.getTime())) return false;

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    if (birthDate) {
      today.setHours(0, 0, 0, 0);

      const selectedDate = new Date(birthDate);
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        return false
      }
    }

    return age < 18;
  };

  const onVideoEnd = React.useCallback(() => {
    setStatusVideo(true);
  }, []);

  const validateSteps = React.useMemo(() => ({
    1: !viewVideo,
    2: !term,
    3: false,
    4: false
  }), [viewVideo, term]);

  const getIsDisabled = React.useCallback((step) => {
    return validateSteps[step] || false;
  }, [validateSteps]);



  const handleValidateVideoApresentation = React.useCallback(async () => {
    if (!statusVideo) {
      showNotification({
        message: 'Você precisa ver o vídeo para avançar',
        supportMessage:
          'É necessário ver todo o vídeo para continuar a inscrição.',
        secondsToClose: 5,
        color: 'default',
      });
      return false;
    }


    if (!viewVideo) {
      showNotification({
        message: 'Você confirmar',
        supportMessage:
          'É necessário ver todo o vídeo para continuar a inscrição.',
        secondsToClose: 5,
        color: 'default',
      });
      return false;
    }

    try {
      await updateConditionViewVideo(interview?.id, {
        viewedVideo: viewVideo,
      });
      return true;
    } catch (error) {
      showNotification({
        message: error.message ?? 'Erro ao salvar',
        secondsToClose: 4,
        color: 'destructive',
      });
      return false;
    }
  }, [viewVideo, updateConditionViewVideo, interview, statusVideo, showNotification]);


  const handleValidateAceptedTerms = React.useCallback(async () => {
    if (!term) {
      showNotification({
        message: 'Você precisa aceitar os termos',
        supportMessage:
          'Aceite os termos para dar continuidade a sua inscrição.',
        secondsToClose: 5,
        color: 'default',
      });
      return false;
    }

    try {
      await updateConditionAceptedTerm(interview?.id, {
        acceptedTerm: term,
      });
      return true;
    } catch (error) {
      showNotification({
        message: 'Erro ao salvar',
        secondsToClose: 4,
        color: 'destructive',
      });
      return false;
    }
  }, [term, updateConditionAceptedTerm, interview, showNotification]);

  const handleValidateFormData = React.useCallback(async () => {
    const errors = {};

    if (!formData?.birthDate || formData.birthDate.trim() === '') {
      errors.birthDate = 'Este campo é obrigatório';
    }

    if (!formData?.phone || formData.phone.trim() === '') {
      errors.phone = 'Este campo é obrigatório';
    } else {
      const cleanPhone = formData.phone.replace(/\D/g, '');
      if (cleanPhone.length < 10) {
        errors.phone = 'Telefone inválido';
      }
    }

    if (
      !formData?.emergencyContactName ||
      formData.emergencyContactName.trim() === ''
    ) {
      errors.emergencyContactName = 'Este campo é obrigatório';
    }

    if (
      !formData?.emergencyContactPhone ||
      formData.emergencyContactPhone.trim() === ''
    ) {
      errors.emergencyContactPhone = 'Este campo é obrigatório';
    } else {
      const cleanPhone = formData.emergencyContactPhone.replace(/\D/g, '');
      if (cleanPhone.length < 10) {
        errors.emergencyContactPhone = 'Telefone inválido';
      }
    }

    if (!radioId) {
      errors.professionalSituation = 'Selecione uma situação profissional';
    }
    if (isUnderage(formData.birthDate) && !aceptEmancipated) {
      errors.emancipated = 'Confirme que voce é emancipado';
    }

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return false;
    }

    const intervDTO = {
      ...interview,
      phone: formData?.phone,
      candidate: {
        ...interview?.candidate,
        birthDate: formData?.birthDate.replaceAll('/', '-').split('-').reverse().join('-'),
        yearParticipatedSeminary:
          year.label === 'Selecione' ? null : year.label,
        emergencyContact: {
          ...interview?.candidate.emergencyContact,
          emergencyContactName: formData.emergencyContactName,
          emergencyContactPhone: formData.emergencyContactPhone.replace(/\D/g, ""),
        },
        emancipated: aceptEmancipated,
        professionalSituation: { id: radioId },
      },
    };
    if (interview.module.id !== 2) {
      delete intervDTO.candidate.yearParticipatedSeminary;
    }


    try {
      const response = await saveBasicDataInterview(intervDTO);
      if (!response) {
        return false;
      }

      return true;
    } catch (error) {
      console.error(error);
      handleError('Erro ao salvar dados!');
      return false;
    }
  }, [
    handleError,
    formData,
    radioId,
    year.label,
    saveBasicDataInterview,
    aceptEmancipated,
    interview,
  ]);

  return (
    <ValidationsContext.Provider
      value={{
        statusVideo,
        setStatusVideo,
        onVideoEnd,
        handleValidateVideoApresentation,
        handleValidateAceptedTerms,
        setTerm,
        term,
        handleValidateFormData,
        viewVideo,
        setViewVideo,
        formErrors,
        setFormErrors,
        setAceptEmancipated,
        aceptEmancipated,
        setYear,
        getIsDisabled,
        isUnderage
      }}
    >
      {children}
    </ValidationsContext.Provider>
  );
}

export function useValidationsContext() {
  const context = React.useContext(ValidationsContext);

  if (!context) {
    throw new Error(
      'useValidationsContext deve ser usado dentro de ValidationsProvider',
    );
  }

  return context;
}
